import React, { useEffect, useState } from "react";
import { EditOutlined, LockOutlined } from '@ant-design/icons';

import SOURCES from "../../../../utils/constants";
import LocalStorageService from "../../../../utils/localStorageServices";
import {
    formatDateTime,
} from "../../../../utils/functions";
import { useGenericQuery } from "../../../../queries/use-generic-query";
import CommonSkeletonLoader from "../../../global/loading";
import { Button, message, Tag, Form } from "antd";
import CommonForm from "../../../global/form";
import CommonCard from "../../../global/card";
import { FiEdit, FiX } from "react-icons/fi";
import CommonDialog from "../../../global/dialog";
import { useGenericMutation } from "../../../../queries/use-generic-mutation";
import { MSG_PROFILE } from "../../../../utils/message";
import { useQueryClient } from "@tanstack/react-query";
import { useHistory } from "react-router-dom";

const localStorageServices = LocalStorageService.getService();

const EnhancedProfileView = ({ onEdit, userData, isLoading }) => {
    const currentUser = localStorageServices.getCurrentUser();
    const isTrialUser = currentUser?.user?.isTrial;
    const userRole = currentUser?.user?.userRole;
    const [cancelPlan, setCancelPlan] = useState(false);

    const [paymentStatus, setPaymentStatus] = useState("Pending");
    const [paymentBadge, setPaymentBadge] = useState("warning");
    const [planType, setPlanType] = useState("Free");
    const [couponId, setCouponId] = useState(null);
    const [form] = Form.useForm();
    const isOtherSelected = Form.useWatch('Other', form);

    const cancellationFormData = [
        { label: "Service Not Working", name: "ServiceNotWorking", type: "checkbox", inputType: SOURCES.FORMINPUTTYPES.INPUT, span: 24 },
        { label: "Not Enough Leads Incoming", name: "NotEnoughLeadsIncoming", type: "checkbox", inputType: SOURCES.FORMINPUTTYPES.INPUT, span: 24 },
        { label: "Too Complicated", name: "TooComplicated", type: "checkbox", inputType: SOURCES.FORMINPUTTYPES.INPUT, span: 24 },
        { label: "Too Expensive", name: "TooExpensive", type: "checkbox", inputType: SOURCES.FORMINPUTTYPES.INPUT, span: 24 },
        { label: "Changed/Cancelled CRM Software", name: "ChangedCancelledCRMSoftware", type: "checkbox", inputType: SOURCES.FORMINPUTTYPES.INPUT, span: 24 },
        { label: "No Longer Using 3rd Party Leads", name: "NoLongerUsing3rdPartyLeads", type: "checkbox", inputType: SOURCES.FORMINPUTTYPES.INPUT, span: 24 },
        { label: "Other", name: "Other", type: "checkbox", inputType: SOURCES.FORMINPUTTYPES.INPUT, span: 24 },
        ...(isOtherSelected ? [{
            label: "Additional Details",
            name: "cancelReasonOther",
            type: "textarea",
            inputType: SOURCES.FORMINPUTTYPES.INPUT,
            required: true,
            message: "Please specify your reason",
            span: 24
        }] : [])
    ];

    const queryClient = useQueryClient()


    const planCancel = useGenericMutation({
        endpoint: `payment/cancel/subscription`,
        onSuccess: () => {
            message.success(MSG_PROFILE.CANCLE_PLAN);
            queryClient.invalidateQueries({ queryKey: ["user", "detail", "me", currentUser.user.email] });
        },
        onError: (err) => {
            message.error(err?.response?.data?.message || "Error cancelling plan");
        }
    });


    const { data: couponData } = useGenericQuery({
        endpoint: `coupon/detail/${couponId}`,
        queryKey: ["coupon", "detail", couponId],
        enabled: !!couponId
    });

    useEffect(() => {
        if (userData) {
            const user = userData;

            setPaymentBadge(user.paymentStatus === true ? "success" : "warning");
            setPaymentStatus(user.paymentStatus === true ? "Completed" : "Pending");
            setPlanType(user.PaymentPlanInfo?.[0]?.planName || "");

            if (user.couponId) setCouponId(user.couponId);
        }
    }, [userData]);

    if (isLoading) {
        return <CommonSkeletonLoader type="profile" rows={1} />;
    }

    const user = userData;

    const cancelPlanAction = async () => {
        try {
            const values = await form.validateFields();

            // Validate that at least one reason is checked
            const hasReason = Object.keys(values).some(key =>
                key !== 'cancelReasonOther' && values[key] === true
            );

            if (!hasReason) {
                message.warning("Please select at least one reason for cancellation.");
                return;
            }

            // Sync with cancelSubscription.jsx logic
            const cancelReasons = {
                ServiceNotWorking: !!values.ServiceNotWorking,
                NotEnoughLeadsIncoming: !!values.NotEnoughLeadsIncoming,
                TooComplicated: !!values.TooComplicated,
                TooExpensive: !!values.TooExpensive,
                ChangedCancelledCRMSoftware: !!values.ChangedCancelledCRMSoftware,
                NoLongerUsing3rdPartyLeads: !!values.NoLongerUsing3rdPartyLeads,
                Other: !!values.Other,
            };

            await planCancel.mutateAsync({
                cancelReasons,
                cancelReasonOther: values.cancelReasonOther || ""
            });

            setCancelPlan(false);
            form.resetFields();
        } catch (err) {
            console.error("Cancellation failed", err);
        }
    };


    return (
        <div className="w-full space-y-6">
            {/* Header Card */}
            {/* <div className="bg-white border border-gray-100 rounded-2xl p-6 shadow-sm flex items-center gap-6">
               
            </div> */}
            <CommonCard>
                <div className="flex flex-col md:flex-row justify-between items-center md:items-start w-full gap-6">
                    <div className="flex flex-col md:flex-row items-center gap-4 md:gap-6 text-center md:text-left w-full md:w-auto">
                        <div className="relative">
                            <div className="size-24 rounded-full !bg-primary border-4 border-white shadow-sm flex items-center justify-center overflow-hidden">
                                <img
                                    src={`https://ui-avatars.com/api/?name=${user?.name}&background=2dbdf2&color=ffffff&size=128`}
                                    alt={user?.name}
                                    className="size-full object-cover"
                                />
                            </div>
                            {/* <div className="absolute bottom-1 right-1 size-6 bg-white rounded-full border border-gray-100 shadow-sm flex items-center justify-center text-[10px] text-gray-500">
                            <FontAwesomeIcon icon={faUser} />
                        </div> */}
                        </div>
                        <div className="space-y-1">
                            <h2 className="text-3xl font-bold text-gray-900 tracking-tight">{user?.name}</h2>
                            <div className="flex flex-col">
                                <span className="text-gray-500 font-medium">{user?.email}</span>
                                <span className="text-xs font-semibold text-primary/80 uppercase tracking-widest mt-1">{userRole}</span>
                            </div>
                        </div>
                    </div>

                    <div className="flex flex-col items-stretch w-full md:w-auto md:items-end gap-3 mt-4 md:mt-0">
                        <Button
                            type="primary"
                            icon={<EditOutlined />}
                            onClick={() => onEdit('EDIT_PROFILE')}
                            className="flex items-center justify-center gap-x-1 text-primary w-full hover:!text-white hover:!bg-primary h-10 !bg-transparent !border !border-primary "
                        >
                            Edit Profile
                        </Button>
                        <Button
                            icon={<LockOutlined />}
                            onClick={() => onEdit('CHANGE_PASSWORD')}
                            className="flex items-center justify-center gap-x-1 text-primary w-full hover:!text-white hover:!bg-primary h-10 !bg-transparent !border !border-primary "
                        >
                            Change Password
                        </Button>
                    </div>
                </div>
            </CommonCard>


            {/* Personal Information Section */}
            <CommonCard title={"Personal Information"} titleClassName="text-xl">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-y-6 gap-x-6 md:gap-x-12">
                    <div className="space-y-1">
                        <span className="text-xs font-semibold text-gray-400 uppercase tracking-widest block">Full Name</span>
                        <p className="text-base font-medium text-gray-800">{user?.name}</p>
                    </div>
                    <div className="space-y-1">
                        <span className="text-xs font-semibold text-gray-400 uppercase tracking-widest block">Email Address</span>
                        <p className="text-base font-medium text-gray-800">{user?.email}</p>
                    </div>
                    <div className="space-y-1">
                        <span className="text-xs font-semibold text-gray-400 uppercase tracking-widest block">Phone Number</span>
                        <p className="text-base font-medium text-gray-800">{user?.phone || 'Not Provided'}</p>
                    </div>
                    <div className="space-y-1">
                        <span className="text-xs font-semibold text-gray-400 uppercase tracking-widest block">Company</span>
                        <p className="text-base font-medium text-gray-800">{user?.company || 'Not Provided'}</p>
                    </div>
                    <div className="space-y-1">
                        <span className="text-xs font-semibold text-gray-400 uppercase tracking-widest block">Timezone</span>
                        <p className="text-base font-medium text-gray-800">{user?.timezone?.name || 'Not Provided'}</p>
                    </div>
                    <div className="space-y-1">
                        <span className="text-xs font-semibold text-gray-400 uppercase tracking-widest block">Registered At</span>
                        <p className="text-base font-medium text-gray-800">{user?.createdAt ? formatDateTime(user.createdAt) : 'N/A'}</p>
                    </div>
                </div>
            </CommonCard>


            {/* Subscription Details Section */}
            {userRole !== SOURCES.USER_ROLE_ADMIN && <CommonCard title={"Subscription & Plan"} titleClassName="text-xl" isExtraButton={{
                show: true,
                data: [
                    {
                        title: "Cancel Plan",
                        onClick: () => setCancelPlan(true),
                        icon: <FiX />,
                        className: "flex items-center gap-x-1 !text-red-500 hover:!text-white hover:!bg-red-500 h-10 !bg-transparent !border !border-red-500",
                    },
                    {
                        title: "Change Plan",
                        onClick: () => onEdit('UPDATE_PLAN'),
                        icon: <FiEdit />,
                        className: `flex items-center gap-x-1 text-primary hover:!text-white hover:!bg-primary h-10 !bg-transparent !border !border-primary ${paymentStatus === "Completed" ? "hidden" : ""}`,
                    }
                ]
            }}>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-y-6 gap-x-6 md:gap-x-12">
                    <div className="space-y-1">
                        <span className="text-xs font-semibold text-gray-400 uppercase tracking-widest block">Plan Type</span>
                        <Tag color="processing" className="px-3 py-1 font-bold text-[10px] rounded-full uppercase tracking-tighter">
                            {planType || 'No Active Plan'}
                        </Tag>
                    </div>
                    <div className="space-y-1">
                        <span className="text-xs font-semibold text-gray-400 uppercase tracking-widest block">Payment Status</span>
                        <Tag color={paymentStatus === 'Completed' ? 'success' : 'error'} className="px-3 py-1 font-bold text-[10px] rounded-full uppercase">
                            {paymentStatus}
                        </Tag>
                    </div>
                    {isTrialUser === "y" && (
                        <div className="space-y-1">
                            <span className="text-xs font-semibold text-gray-400 uppercase tracking-widest block">Trial Mode</span>
                            <Tag color="error" className="px-3 py-1 font-bold text-[10px] rounded-full uppercase">
                                Active
                            </Tag>
                        </div>
                    )}
                    {couponData && (
                        <div className="space-y-1">
                            <span className="text-xs font-semibold text-gray-400 uppercase tracking-widest block">Discount Applied</span>
                            <Tag color="error" className="px-3 py-1 font-bold text-[10px] rounded-full uppercase">
                                {couponData?.code}
                            </Tag>
                        </div>
                    )}
                </div>
            </CommonCard>}

            {
                cancelPlan && (
                    <CommonDialog
                        open={cancelPlan}
                        setOpen={setCancelPlan}
                        title="Cancel Subscription"
                        description="Are you sure you want to cancel your subscription?"
                        onOk={cancelPlanAction}
                        onCancel={() => setCancelPlan(false)}
                        okButtonTitle="Confirm"
                        isConfirmButtonLoading={planCancel.isPending}
                        width={500}
                    >
                        <CommonForm
                            form={form}
                            data={cancellationFormData}
                        />
                    </CommonDialog>
                )
            }
        </div >
    );
};

export default EnhancedProfileView;
